import type { Competition, Enum, Matchday, Player, Round, Team, Translatable, Translation } from '@uefadigital/panenka';
import type { Group, MatchPerson } from '@uefadigital/panenka/dist/types/services/interfaces/match-unit';

export interface StatDefinition {
  actors: string[];
  aggregationType: string;
  description: string;
  group: string;
  name: string;
  translations: {
    name: Record<string, string>;
  };
}

export interface Statistics {
  name: string;
  translations: {
    name: Record<string, string>;
  };
  value: string;
}

export interface Ranking {
  player: Player;
  playerId: string;
  statistics: Statistics[];
}

export interface Season {
  competitionId: string;
  endDate: string;
  id: string;
  name: string;
  seasonYear: string;
  shortName: string;
  startDate: string;
  status: string;
}

export interface CompstatsV1TeamRanking {
  statistics: Statistics[];
  team: Team;
  name: Record<string, string>;
}

export interface CompstatsV1TeamStatisticsHead2Head {
  statistics: Statistics[];
  teamId: string;
}

export interface CompstatsV1TeamStatisticsHead2HeadAdvanced {
  statistics: Statistics[];
  teamId: string;
  againstCountryCode: string;
  matchIds: string[];
}

export interface CompstatsV1PlayerRankingLeader {
  name: string;
  rankings: Ranking[];
}

export interface CompV2TeamPlayers {
  playerId: string;
  team: Team;
  teamId: string;
}

export interface CompV2CompetitionStructure {
  competition: Competition;
  matchdays: Matchday[];
  rounds: Round[];
  season: Season;
}

export interface MatchEvents {
  bodyPart: string;
  fieldPosition: {
    coordinate: Record<string, number>;
    distance: number;
    zone: string;
  };
  id: string;
  matchId: string;
  phase: string;
  primaryActor: {
    person: Player;
    team: Team;
    type: string;
  };
  secondaryActor: {
    person: Player;
    team: Team;
    type: string;
  };
  subType?: string;
  time: Record<string, number>;
  timestamp: string;
  totalScore: Record<string, number>;
  type: string;
}

export interface LineUpPlayer {
  fieldCoordinate: Record<string, number>;
  jerseyNumber: number;
  player: Player;
  type: string;
}

export interface LineUpTeam {
  bench: Array<LineUpPlayer>;
  coaches: Array<MatchPerson>;
  field: Array<LineUpPlayer>;
  shirtColor: string;
  team: Team;
}

export interface LineUps {
  awayTeam: LineUpTeam;
  homeTeam: LineUpTeam;
  lineupStatus: string;
  matchId: string;
}

export interface Sponsors {
  code: string;
  competitionId: string;
  image: string;
  links: Record<string, string>;
  mainSponsor: string;
  name: string;
  type: string;
}

export interface Statistic {
  attributes?: Record<string, string>;
  name: string;
  value: string;
  unit?: string;
  translations: {
    name: Record<string, string>;
  };
}

export interface MatchStatistic {
  statistics: Array<Statistic>;
  teamId: string;
}

export interface Stats {
  name: string;
  value: string;
  translations?: {
    name: Record<string, string>;
  };
}

export interface PlayerStatus {
  isBooked?: boolean;
  isSuspended?: boolean;
}

export interface SquadPlayer {
  player: Player;
  availableForNextMatch?: boolean;
  jerseyNumber?: number;
  squadList?: {
    status: string;
    type: string;
  };
  stats?: Array<Stats>;
  status?: PlayerStatus;
}

export interface SquadGroup {
  fieldPosition: string;
  players: Array<SquadPlayer>;
  translations: {
    fieldPosition: Record<string, string>;
  };
  displayHeading?: boolean;
  status: PlayerStatus;
}

export interface CompetitionTeamSquad {
  coaches: Array<MatchPerson>;
  competitionId: string;
  seasonYear: string;
  squad: Array<SquadGroup>;
  teamId: string;
  unlistedPlayers: Array<SquadGroup>;
}

export interface PlayerStatistic {
  playerId: string;
  statistics: Array<Statistic>;
}

export interface TeamStanding {
  drawn: number;
  goalDifference: number;
  goalsAgainst: number;
  goalsFor: number;
  lastRank: number;
  lost: number;
  played: number;
  points: number;
  rank: number;
  qualified: boolean;
  qualificationLabelId?: Qualified;
  rankStatus: string;
  team: Team;
  won: string;
}

export const Qualified = {
  R16: '1',
  R16_PO: '2',
  PO: '3',
  ELIMINATED: '4',
} as const;
export type Qualified = Enum<typeof Qualified>;

export interface QualificationLabel extends Translatable {
  labelId: string;
  translations: {
    labelName: Translation;
  };
}

export interface DomesticTeamsStanding {
  items: Array<TeamStanding>;
}

export interface Players {
  players: Array<SquadPlayer>;
}

export interface Standing {
  group: Group;
  items: Array<TeamStanding>;
  round: Round;
  status: string;
  qualificationLabels?: QualificationLabel[];
}
export interface MatchCalendar {
  data: Array<MatchCalendarDate>;
}

export interface MatchCalendarDate {
  data: Array<TeamStanding>;
  date: string;
  numberOfMatches: number;
}
